import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import MediaCard from "@/components/feature/MediaCard";
import FilterBar, { type FilterValues } from "@/components/feature/FilterBar";
import { usePageMeta } from "@/hooks/usePageMeta";
import {
  filterMedia,
  movieGenres,
  regions,
  searchMedia,
  topByViews,
  tvGenres,
  years,
} from "@/lib/media";

const kindTabs = [
  { value: "all", label: "全部" },
  { value: "movie", label: "电影" },
  { value: "tv", label: "电视剧" },
];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q") || "";
  const type = searchParams.get("type") || "all";

  const [input, setInput] = useState(keyword);

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  usePageMeta({
    title: keyword
      ? `“${keyword}” 搜索结果 - 免费观看大片在线观看`
      : "搜索影片 - 免费观看大片在线观看",
    description: `在免费观看大片在线观看站内搜索“${keyword}”相关的电影、电视剧资源，支持按类型、地区、年份筛选，快速找到想看的影片。`,
    keywords: `${keyword},影片搜索,在线电影,电视剧资源,免费观看大片在线观看`,
  });

  const values: FilterValues = {
    genre: searchParams.get("genre") || "",
    region: searchParams.get("region") || "",
    year: searchParams.get("year") || "",
    sort: "default",
  };

  const updateParams = (patch: Record<string, string>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (value) next.set(key, value);
      else next.delete(key);
    });
    setSearchParams(next);
  };

  const handleSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    updateParams({ q: input.trim(), genre: "", region: "", year: "" });
  };

  const updateFilters = (patch: Partial<FilterValues>) => {
    updateParams({
      genre: patch.genre ?? values.genre,
      region: patch.region ?? values.region,
      year: patch.year ?? values.year,
    });
  };

  const resetFilters = () => updateParams({ genre: "", region: "", year: "" });

  const kind = type === "movie" || type === "tv" ? type : undefined;
  const baseResults = keyword ? searchMedia(keyword, kind) : [];
  const results = filterMedia(baseResults, values);
  const hasFilters = Boolean(values.genre || values.region || values.year);

  const genreOptions = (() => {
    if (type === "movie") return movieGenres;
    if (type === "tv") return tvGenres;
    return Array.from(new Set([...movieGenres, ...tvGenres]));
  })();

  const recommendations = [...topByViews("movie", 3), ...topByViews("tv", 3)].slice(0, 6);

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6 md:px-6 md:py-8">
      <nav className="flex items-center gap-1.5 text-sm text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-500">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">搜索结果</span>
      </nav>

      <header className="mt-4">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          影片搜索
        </h1>
        <p className="mt-2 text-sm text-foreground-600">
          {keyword ? (
            <>
              关键词 <span className="font-semibold text-primary-500">“{keyword}”</span> 共找到{" "}
              <span className="font-semibold text-foreground-950">{results.length}</span> 部相关影片
            </>
          ) : (
            "输入片名、演员、类型或地区关键词，即可快速查找影片。"
          )}
        </p>
      </header>

      <form onSubmit={handleSearch} className="mt-5">
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <span className="pointer-events-none absolute left-3.5 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-500">
              <i className="ri-search-line text-lg"></i>
            </span>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              type="text"
              name="keyword"
              placeholder="搜索电影、电视剧、演员、类型"
              className="w-full rounded-md border border-background-200/70 bg-background-100 py-3 pl-11 pr-4 text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500/30"
            />
          </div>
          <button
            type="submit"
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-search-line text-lg"></i>
            搜索
          </button>
        </div>
      </form>

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <span className="text-sm text-foreground-500">类型筛选：</span>
        {kindTabs.map((tab) => (
          <button
            key={tab.value}
            type="button"
            onClick={() => updateParams({ type: tab.value === "all" ? "" : tab.value })}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              type === tab.value
                ? "bg-primary-500 text-background-50"
                : "border border-background-200/70 text-foreground-600 hover:border-primary-400 hover:text-primary-500"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {keyword && (
        <div className="mt-5">
          <FilterBar
            genreOptions={genreOptions}
            regionOptions={regions}
            yearOptions={years}
            values={values}
            onChange={updateFilters}
            onReset={resetFilters}
            resultCount={results.length}
            showSort={false}
          />
        </div>
      )}

      {results.length > 0 ? (
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
          {results.map((item) => (
            <MediaCard key={item.id} media={item} showSynopsis />
          ))}
        </div>
      ) : (
        <div className="mt-8">
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-200/70 bg-background-100/40 py-14 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200/60 text-foreground-500">
              <i className="ri-emotion-sad-line text-2xl"></i>
            </span>
            <p className="mt-4 font-heading text-base font-semibold text-foreground-900">
              {keyword ? `没有找到与“${keyword}”相关的影片` : "还没有输入搜索关键词"}
            </p>
            <p className="mt-1.5 text-sm text-foreground-500">
              {hasFilters
                ? "当前筛选条件下没有结果，试试重置筛选条件"
                : "换个关键词试试，或看看下面这些热门推荐"}
            </p>
            {hasFilters && (
              <button
                type="button"
                onClick={resetFilters}
                className="mt-5 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                重置筛选
              </button>
            )}
          </div>

          <section className="mt-10">
            <h2 className="mb-4 font-heading text-lg font-bold text-foreground-950">
              热门推荐
            </h2>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-6">
              {recommendations.map((item) => (
                <MediaCard key={item.id} media={item} />
              ))}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}